import { BOOK_LABEL } from "./Performance";

// One rule, enforced in the UI as well as the API: a real-money figure and a
// PAPER figure never share a headline. Alan (2026-08-19), seeing the firm-wide
// sleeve card while on Live·Daily: "we don't have those crypto balances and
// did we really lose 500 on options????" — both numbers were paper.

export type Money = "real" | "paper" | "all";

export function MoneySwitch({ book, money, onChange }: { book: string; money: Money; onChange: (m: Money) => void }) {
  if (book !== "all") return null;
  return (
    <div className="flex gap-1 text-xs font-mono">
      {(["real", "paper"] as const).map((m) => (
        <button key={m} onClick={() => onChange(m)}
          className={`px-2 py-0.5 rounded border ${money === m
            ? (m === "real" ? "border-down text-down" : "border-slate-400 text-slate-100")
            : "border-slate-700 text-slate-500 hover:text-slate-300"}`}>
          {m === "real" ? "Real money" : "Paper"}
        </button>
      ))}
    </div>
  );
}

/** What the numbers below actually cover — always stated, never implied. */
export function ScopeNote({ book, money, isPaper, books }: { book: string; money: Money; isPaper: boolean; books?: string[] }) {
  const what = book !== "all"
    ? (BOOK_LABEL[book] ?? book)
    : money === "real" ? "every live book" : "every paper book";
  return (
    <div className="flex items-center gap-2 text-[10px] mt-1">
      <span className={`badge ${isPaper ? "bg-slate-700 text-slate-300" : "bg-down/15 text-down"}`}>
        {isPaper ? "PAPER — simulated" : "REAL MONEY"}
      </span>
      <span className="text-slate-500">
        {what}
        {/* name the books only when "every live/paper book" doesn't already say it */}
        {book === "all" && books?.length ? ` · ${books.map((b) => BOOK_LABEL[b] ?? b).join(", ")}` : ""}
      </span>
    </div>
  );
}

/** Normalise anything the backend calls money into "real" | "paper" | null.
    Was an exact-string compare (`a.money === "REAL MONEY"`) in three places —
    a payload saying "real" silently rendered as paper, with no badge. */
export function readMoney(v: unknown): "real" | "paper" | null {
  const t = String(v ?? "").trim().toLowerCase();
  if (!t) return null;
  if (t.includes("real") || t === "live") return "real";
  if (t.includes("paper") || t.includes("sim")) return "paper";
  return null;
}

/** Inline money badge. `null` renders an explicit UNKNOWN rather than nothing:
    a dollar figure with no money type beside it is the bug, not the fallback. */
export function MoneyBadge({ money, className = "" }: { money: "real" | "paper" | null; className?: string }) {
  const spec = money === "real"
    ? { c: "bg-down/15 text-down", t: "REAL MONEY" }
    : money === "paper"
      ? { c: "bg-slate-700 text-slate-300", t: "PAPER — simulated" }
      : { c: "bg-gold/15 text-gold", t: "MONEY TYPE UNKNOWN" };
  return <span className={`badge ${spec.c} ${className}`}>{spec.t}</span>;
}
